import { defineComponent as _defineComponent } from 'vue'
import { useServiceStore } from "@/stores/serviceStore";
import { PropType, computed, ref } from "vue";
import { IOption, RankCode, TimerStatus } from "@/types";
import { Form } from "vee-validate";
import DynamicFormField from "./DynamicFormField.vue";
import BaseButton from "../shared/button/base/BaseButton.vue";
import { ISignupField } from "@/domain/entities/serviceInfo/serviceInfo.types";


const __default__ = {
  name: "DynamicSignupForm",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  rankCode: {
    type: String as PropType<RankCode>,
    required: true,
  },
},
  emits: ["onClickSignupButton"],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const serviceStore = useServiceStore();

const hasUnverifiedFields = () => {
  return !!serviceStore.getUnverifiedFields(props.rankCode);
};

const getSchema = (field: ISignupField): Record<string, any> => {
  const passwordPlaceholder = field.placeholder
    ?.split("(")?.[1]
    ?.replace(")", "");
  const passwordErrorMessage = passwordPlaceholder
    ? passwordPlaceholder + "으로 입력해주세요."
    : "비밀번호 양식에 맞게 입력해주세요.";
  const passwordScheme = "@JOIN.PASSWORD";

  return {
    "JOIN.MEMBER_NAME": { required: "이름을" },
    "JOIN.CLIENT_COMPANY": { required: "소속회사를" },
    "JOIN.WORK_TIME": { required: "근무형태를" },
    "JOIN.LOGINID": { required: "아이디를" },
    "JOIN.MEMBER_EMAIL": { required: "이메일을", email: true },
    "JOIN.MEMBER_EMPLOYEE_NO": { required: "사번을" },
    "JOIN.MEMBER_PHONE": { required: "휴대폰 번호를", phone: true },
    "JOIN.MEMBER_DEPT_NAME": { required: "부서를" },
    "JOIN.HOME_ADDRESS": { required: "자택 주소를" },
    "JOIN.DUTY_MANAGER_NAME": { required: "이름을" },
    "JOIN.DUTY_MANAGER_DEPT_NAME": { required: "부서를" },
    "JOIN.BP_NAME": { required: "협력사 이름을" },
    "JOIN.DUTY_MANAGER_EMAIL": { required: "이메일을", email: true },
    "JOIN.CLIENT_COMPANY_BP": { required: "근무회사를" },
    "JOIN.DUTY_NOTE": { required: "담당업무를" },
    "JOIN.DUTY_MANAGER_CLIENT_COMPANY": { required: "소속회사를" },
    "JOIN.DUTY_MANAGER_PHONE": { required: "연락처를" },
    "JOIN.PASSWORD": {
      required: "비밀번호를",
      password: [field.passwordRegex, passwordErrorMessage],
    },
    emailCertNo: { required: "인증번호를" },
    passwordCert: {
      required: "비밀번호를 다시",
      passwordConfirm: passwordScheme,
    },
    phoneCertNo: { required: "인증번호를" },
  };
};

const getRules = (field: ISignupField) => {
  if (!field.isRequired || field.isAutoFilledForJoin || !field.key) return;

  const schema = getSchema(field);
  return schema[field.key];
};

const signUpFields = computed(
  () =>
    serviceStore.signupInfos.find((info) => info.useRankCode === props.rankCode)
      ?.fields
);

const arrangedSignUpFields = computed(() => {
  const filteredSignUpFields: ISignupField[] = [];
  signUpFields.value?.forEach((field) => {
    if (field.parentFieldKey) {
      const parentField = signUpFields.value?.find(
        (signUpField) => signUpField.key === field.parentFieldKey
      );
      if (parentField?.isAutoFilledForJoin) return;
    }

    if (field.isCertRequired && field.isAutoFilledForJoin) {
      filteredSignUpFields.push({ ...field, isCertRequired: false });
    } else {
      filteredSignUpFields.push(field);
    }
  });

  return filteredSignUpFields;
});

const updateFieldEdited = (field: ISignupField) => {
  if (field.isEdited) return;
  serviceStore.updateFieldEdited(props.rankCode ?? "", field?.key ?? "", true);
};

const updateValue = (field: ISignupField, value: any) => {
  updateFormValue(field, value);
  updateFieldEdited(field);
};

const formValues = ref<Record<string, any>>({});
const getFieldValue = (inputFields?: string[]) => {
  return inputFields ? formValues.value[inputFields[0]] : undefined;
};

const getParentFieldValue = (field: ISignupField) => {
  const parentField = getParentField(field);
  if (!parentField || !parentField.inputFields) {
    return "";
  }
  return formValues.value[parentField.inputFields[0]];
};

const getParentField = (field: ISignupField) => {
  return arrangedSignUpFields.value?.find(
    (signupField) => signupField.key && signupField.key === field.parentFieldKey
  );
};

const getChildField = (field: ISignupField) => {
  return arrangedSignUpFields.value?.find(
    (signupField) =>
      signupField.parentFieldKey && signupField.parentFieldKey === field.key
  );
};

const parseSubOptions = (data: any): IOption => {
  const option: IOption = {
    name: data,
    value: data,
  };
  return option;
};

const updateChildFieldOptions = (field: ISignupField, value: any) => {
  const selectedOption = field.options?.find(
    (option) => option.value === value
  );
  const childField = getChildField(field);
  if (!selectedOption?.data || !childField?.key || !childField.subOptionsKey)
    return;

  serviceStore.updateFieldOptions(
    props.rankCode,
    childField.key,
    selectedOption.data[childField.subOptionsKey].map(parseSubOptions)
  );
};

const updateFormValue = (field: ISignupField | undefined, value: any) => {
  if (!field?.inputFields?.length) return;

  field.inputFields.forEach((inputField) => {
    formValues.value[inputField] = value;
  });

  if (field.type !== "option") return;
  updateChildFieldOptions(field, value);
};

const updateFormValueWithFieldKey = (fieldKey: string, value: any) => {
  const targetField = arrangedSignUpFields.value.find(
    (field) => field.key === fieldKey
  );
  if (!targetField) return;

  updateFormValue(targetField, value);
};

const updateChildTimerStatus = (
  field: ISignupField,
  timerStatus: TimerStatus
) => {
  const childField = getChildField(field);
  serviceStore.updateFieldTimerStatus(
    props.rankCode ?? "",
    childField?.key ?? "",
    timerStatus
  );
};

const onClickSignupButton = () => {
  emit("onClickSignupButton", formValues.value);
};

__expose({ updateFormValueWithFieldKey });

const __returned__ = { props, emit, serviceStore, hasUnverifiedFields, getSchema, getRules, signUpFields, arrangedSignUpFields, updateFieldEdited, updateValue, formValues, getFieldValue, getParentFieldValue, getParentField, getChildField, parseSubOptions, updateChildFieldOptions, updateFormValue, updateFormValueWithFieldKey, updateChildTimerStatus, onClickSignupButton, get Form() { return Form }, DynamicFormField, BaseButton }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})